import * as THREE from 'three';
const { tan, cos, atan, acos, min, max, pow, PI, sqrt } = Math;
import clamp from '../utils/clamp';
import decompose from '../utils/decompose';
import { getCamera, getCameraWorldTransform, getRect } from '../../modules/player';
import { set, get, getWorldTransform } from '../../modules/scene';
import { merge, virtualTargetId } from '../../modules/sceneGraph';
import updateCameraLookAtTarget from '../utils/updateCameraLookAtTarget';
import getTargetWorldMatrix from '../utils/getTargetWorldMatrix';

const EPS = 0.000001;
const cameraPosition = new THREE.Vector3();
const targetPosition = new THREE.Vector3();
const targetToCamera = new THREE.Vector3();

export default function zoom(store, translator) {
  function zoomBy({ delta, deltaX, deltaY }) {
    const d = delta !== undefined ? delta : ((deltaX / 30) + (-deltaY / 30));
    const cameraId = getCamera(store);
    const cameraWorldMatrix = getWorldTransform(store, cameraId);
    updateZoom(store, -d, cameraId, cameraWorldMatrix);
    store.dispatch(merge([cameraId, 'Transform', 0], decompose(cameraWorldMatrix)));
  }

  return {
    label: 'Zoom',
    enabled: true,
    display: {
      playerTools: true,
    },
    options: {},

    tool: {
      drag: function (ev) {
        if (!this.active && !ev.ctrlKey) return false;

        return {
          momentum: true,
          handle: function (ev) {
            const newEv = { delta: ev.delta !== undefined ? ev.delta : ((ev.deltaX / 30) + (-ev.deltaY / 30)) };
            zoomBy(newEv);
          },
        };
      },

      pinch: function (ev) {
        zoomBy(ev);
      },

      scroll: function (ev) {
        zoomBy(ev);
      },
    },
  };
}

export function updateZoom(store, mouseDelta, cameraId, cameraWorldMatrix) {
  const cameraData = get(store, { id:  cameraId, evalPlug: 'Camera' });
  const playerData = get(store, { scene: true, evalPlug: 'Player' });
  const targetWorldMatrix = getTargetWorldMatrix(store, cameraId);
  const rect = getRect(store);

  const deltaZoom = mouseDelta / rect.y;
  const decayZoomFactor = pow(2, deltaZoom * 100);  // this ensures the zoom has consistent speed no matter the size

  if( cameraData.projection === 'Perspective') {

      cameraPosition.setFromMatrixPosition(cameraWorldMatrix);
      targetPosition.setFromMatrixPosition(targetWorldMatrix);

      targetToCamera.copy(cameraPosition).sub(targetPosition);
      const targetToCameraDistance = max(targetToCamera.length(), EPS);
      let newTargetToCameraDistance = targetToCameraDistance * decayZoomFactor;

      if (cameraData.radiusConstraint) {
        const { maxBoundingSphere, minBoundingSphere } = cameraData.radiusConstraint;
        const camToTargetBackward = new THREE.Vector3().copy(targetToCamera).normalize();
        const camToTargetForward = new THREE.Vector3().copy(camToTargetBackward).multiplyScalar(-1);

        const forwardRay = new THREE.Ray(cameraPosition, camToTargetForward);
        const backwardRay = new THREE.Ray(cameraPosition, camToTargetBackward);

        let lowerBound = new THREE.Vector3(), upperBound = new THREE.Vector3();

        forwardRay.intersectSphere(minBoundingSphere, lowerBound);
        backwardRay.intersectSphere(maxBoundingSphere, upperBound);

        const minZoom = lowerBound.sub(targetPosition).length();
        const maxZoom = upperBound.sub(targetPosition).length();

        newTargetToCameraDistance = clamp(newTargetToCameraDistance, minZoom + __BOUNDING_BUFFER__, maxZoom - __BOUNDING_BUFFER__);
      }

      targetToCamera.multiplyScalar(newTargetToCameraDistance / targetToCameraDistance);
      cameraPosition.copy(targetPosition).add(targetToCamera);
      if (cameraPosition.y < 0 && playerData.constraintCameraY && playerData.orbitMode !== 'trackball') {
        const thirdVector = new THREE.Vector3().crossVectors(targetToCamera, cameraData.upPosition).normalize();
        cameraPosition.y = 0;
        targetToCamera.copy(cameraPosition).sub(targetPosition);
      }

      cameraWorldMatrix.setPosition(cameraPosition);

      updateCameraLookAtTarget(cameraWorldMatrix, targetWorldMatrix, cameraData.upPosition);

  } else if(cameraData.projection === 'Orthographic') {
    const zoom = get(store, {id: cameraId, plug: 'Camera', property:'orthoZoom'});
    store.dispatch(set({id: cameraId, plug: 'Camera', property:'orthoZoom'},  zoom * decayZoomFactor));
  }

};




// WEBPACK FOOTER //
// ./src/translators/tools/zoom.js